import React from "react";
import { useState, useCallback, useEffect } from "react";

export interface NumericInputProps {
  /**
   * Value of the field. (This is a controlled component)
   */
  value: number | undefined;

  /**
   * Handler for when the value changes. Will be called when the input control loses focus.
   */
  onChange: (val: number | undefined) => void;

  id?: string;
  name?: string;
  max?: number;
  min?: number;
  step?: number;
  disabled?: boolean;

  numberType?: "integer" | "float";

  /**
   * Number will be rounded using this e notation. Defaults to -2, i.e. round to 2 decimal places:
   * 1.005e-2 = 1.01
   */
  roundingExponent?: number;
}

/**
 * Number input field for inputting integers or floats. Built around `<input type="numeric" />`.
 * 
 * The styling is intentionally basic to help with flexibility. To have number input on your form, instead try the `NumberStepper` component.
 * 
 * @status stable
 */
export const NumericInput = (props: NumericInputProps) => {
  const parseValue = useCallback((val: string | number | undefined) => {
    if (val == null || val.toString().trim() === "") {
      return undefined;
    }

    if (isNaN(val as number)) {
      val = "0";
    }

    const exp = props.numberType === "float" ? props.roundingExponent ?? -2 : 0;
    const rounded = Number(Math.round(+`${parseFloat(val as string)}e${-exp}`) + `e${exp}`);
    const clamped = Math.min(props.max ?? Infinity, Math.max(props.min ?? -Infinity, rounded));
    return clamped;
  }, [props.numberType, props.roundingExponent]);

  const propValue = parseValue(props.value?.toString())?.toString();
  const [hasFocus, setHasFocus] = useState(false);
  const [focusValue, setFocusValue] = useState<typeof propValue>();

  const onFocus = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    setHasFocus(true);
    setFocusValue(propValue);
    e.currentTarget.select();
  }, [propValue]);

  const onChange = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    // Use a local state var while focussed to allow invalid/unfinished input e.g. "0." "1,000"
    if (hasFocus) {
      setFocusValue(e.currentTarget.value);
    }
    else {
      props.onChange(parseValue(e.currentTarget.value));
    }
  }, [hasFocus, props.onChange]);

  const onBlur = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    setHasFocus(false);
    setFocusValue(undefined);
    props.onChange(parseValue(e.currentTarget.value));
  }, [props.onChange]);

  const onKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (!(/[0-9,.]/.test(e.key))) {
      e?.preventDefault();
    }
  }, []);

  // On mount, props.value might be invalid (e.g. not within min/max range).
  useEffect(() => {
    const parsedValue = parseValue(propValue ?? "");
    if (parsedValue !== props.value) {
      props?.onChange(parsedValue);
    }
  }, []);

  return (
    <input
      id={props.id}
      name={props.name}
      type="number"
      placeholder={`${props.min ?? 0}`}
      value={(hasFocus ? focusValue : propValue) ?? ""}
      min={props.min}
      max={props.max}
      step={props.step ?? "any"}
      disabled={props.disabled}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={onChange}
      onKeyPress={onKeyPress}
    />
  );
};

